import { TONES } from "@/lib/ui/tones";
import { cn } from "@/lib/utils";

/**
 * The side nav's shared class strings (docs/design-guidelines.md §12).
 *
 * Three surfaces, and nothing else spells their recipes out:
 *   • the navy frame (the top bar and the icon rail, wide screens only) —
 *     muted navy text, a white tint when open or hovered, white text, a coral
 *     icon and a coral bar for the current menu;
 *   • the cream panel, panes and phone header — stone hover, a coral-tinted
 *     pill with a coral icon when current;
 *   • the white sheets (the mobile drawer, the account menu) — coral-50 hover
 *     and pill.
 */

// The icon rail's width. Wide enough that "My Results" fits on one line.
export const NAV_RAIL_WIDTH = "w-[5.5rem]";

// Search opens at this width beside the rail, and Sabine's chat at the right
// edge, both wider than the menu panel: a reply with a table in it needs the
// room. The top bar gives her header the same width, right above her column.
export const NAV_COLUMN_WIDTH = "w-[26rem]";

// A piece that comes in, in order, when the column beside the rail changes
// (app/globals.css `.nav-arrive`): `arriveStep(n)` is its place in that
// order, as an inline style.
export const NAV_ARRIVE = "nav-arrive";
export const arriveStep = (step) => ({ "--nav-step": step });

// The navy top bar stays at the top of the screen on wide screens; the rail,
// the panel and Sabine's column stick under it and fill the rest.
export const NAV_TOP_BAR_HEIGHT = "h-14";
export const NAV_BELOW_TOP_BAR =
  "md:sticky md:top-14 md:h-[calc(100dvh-3.5rem)] md:self-start";
// While the bar is mounted, app/globals.css sets --app-bar-height to its
// height (zero where there is no bar: phones, signed-out pages, the desktop
// app). A page that pins its own header reads it, so the header sticks under
// the bar, not behind it.
export const STICKY_PAGE_TOP = "sticky top-[var(--app-bar-height,0px)]";
// A page's own sidebar that sticks beside its content on wide screens. It is
// as tall as the screen under the bar, so it must also start there: AppShell's
// top padding (--app-content-pad-y, app/globals.css) would otherwise push its
// bottom edge that far below the fold until the page scrolled (SAL-3236).
// A page that pins a strip of its own above the sidebar sets
// --app-above-height to the strip's height and --app-content-pad-y to 0px for
// the layout, so the sidebar starts under the strip and still ends at the
// bottom of the window.
export const STICKY_PAGE_SIDEBAR =
  "md:sticky md:top-[calc(var(--app-bar-height,0px)+var(--app-above-height,0px))] md:-mt-[var(--app-content-pad-y,0px)] md:h-[calc(100dvh-var(--app-bar-height,0px)-var(--app-above-height,0px))] md:self-start";

// The nav's coral, in three roles, so the navy and the cream show one orange.
// A mark (an icon, a bar, a chevron, the live field's border) is brand-500 on
// both: the rail's coral. Words on cream are brand-800, because brand-500
// measures 2.6:1 there and text needs 4.5:1; on the navy brand-500 words read
// at 6.3:1, so the frame's words are marks too. A fill is the rail's coral
// at a tint, under brand-800 words. No other shade is written in
// components/nav outside this file (sideNavFrame.test.js).
export const NAV_CORAL_MARK = "text-brand-500";
export const NAV_CORAL_BAR = "bg-brand-500";
export const NAV_CORAL_TEXT = "text-brand-800";
export const NAV_CORAL_FILL = cn("bg-brand-500/13", NAV_CORAL_TEXT);

// The navy frame and the controls on it.
export const NAV_INK = "bg-surface-chrome-ink";
export const NAV_INK_REST =
  "text-navy-300 hover:bg-white/5 hover:text-white";
export const NAV_INK_OPEN = "bg-white/10 text-white";
// The rail button for the menu the page belongs to: white text, a coral
// glyph and a coral bar on the rail's left edge. Spelled out rather than
// built from NAV_CORAL_*, because Tailwind only sees whole variant classes.
export const NAV_INK_CURRENT =
  "text-white [&>svg]:text-brand-500 before:absolute before:-left-1.5 before:inset-y-2.5 before:w-[3px] before:rounded-r-full before:bg-brand-500";
// Focus on the navy frame: the coral ring, offset onto navy rather than white.
export const NAV_INK_FOCUS_RING =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900";
// A ghost button on the navy frame: Help, and the chat's header controls
// while they sit in the top bar.
export const NAV_INK_ICON_BUTTON = cn(
  "flex h-10 w-10 items-center justify-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-40",
  NAV_INK_REST,
  NAV_INK_FOCUS_RING,
);

// Hidden until hovered, then thin, so a long menu scrolls without a bar
// dividing the cream panel from the sheet.
export const NAV_SCROLLBAR =
  "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden " +
  "hover:[scrollbar-width:thin] hover:[scrollbar-color:theme(colors.stone.300)_transparent] " +
  "hover:[&::-webkit-scrollbar]:block hover:[&::-webkit-scrollbar]:w-1 " +
  "hover:[&::-webkit-scrollbar-track]:bg-transparent " +
  "hover:[&::-webkit-scrollbar-thumb]:rounded-full " +
  "hover:[&::-webkit-scrollbar-thumb]:bg-stone-300";

// The 10px label type: the panel's header and the label above a group of
// links. Deliberately not <Eyebrow>, which is the 11px/0.18em eyebrow for
// content sections.
export const NAV_EYEBROW_TYPE =
  "text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400";
export const NAV_GROUP_EYEBROW = cn("px-3 pb-1.5", NAV_EYEBROW_TYPE);

// The pill marking the current row on the cream panel; its icon takes
// NAV_CORAL_MARK (NavPanelItems).
export const NAV_CURRENT_PILL = cn(NAV_CORAL_FILL, "font-medium");
// Sabine's solid coral, read from the tone table: the person's own bubble in
// Sabine's chat, its send button and its thinking dots. White on
// brand-500 is the one solid coral in the app; a hover darkens it one step.
export const SABINE_SOLID = cn(TONES.brand.fill, "text-white");
export const SABINE_SOLID_HOVER = "hover:bg-brand-600";
export const NAV_REST_ITEM =
  "text-gray-600 hover:bg-stone-200/50 hover:text-gray-900";

// The white field in the navy top bar: the search trigger, and the same box
// once it turns into the input. The one thing on the bar that looks typeable.
// The caller adds its focus ring.
export const NAV_FIELD =
  "flex h-9 w-full items-center gap-2.5 rounded-lg border border-transparent bg-white px-3 text-left text-sm text-gray-400 hover:text-gray-500";
// The same field once it is the search input: its border coral while it is
// live, the back arrow in the icon's place.
export const NAV_FIELD_LIVE =
  "h-9 gap-2 rounded-lg border border-brand-500 bg-white pl-1 pr-3";

// Focus on the cream chrome: the search controls, the phone header's Help and
// the Ask Sabine button on a signed-out page.
export const NAV_FOCUS_RING =
  "transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-800 focus-visible:ring-offset-2";
// The coral tile around a search control's icon: the back arrow inside the
// open field. The caller sets its size.
export const SEARCH_TILE = cn(
  "inline-flex shrink-0 items-center justify-center hover:bg-brand-100 hover:text-brand-700",
  TONES.brand.tile,
  NAV_FOCUS_RING,
);

// A ghost button on the cream chrome: the mobile header's search and menu,
// the chat's header controls, and (widened, with a label) the Ask Sabine
// button above the page.
export const NAV_ICON_BUTTON =
  "flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-stone-200/50 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-40";

/** A link row in the cream panel. */
export function panelRowClass(active) {
  return cn(
    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150",
    active ? NAV_CURRENT_PILL : NAV_REST_ITEM,
  );
}

/**
 * A small button over the right end of a link row, in the cream panel or the
 * drawer: a link's bookmark star, a bookmark's remove (NavBookmarks). The row
 * wraps the link and the button in `group/row relative`, since a button
 * cannot sit inside a link. Hidden until the row is hovered or the button
 * focused; shown at rest when `on` (a starred link), and always on a touch
 * screen, which has no hover to reveal it.
 */
export function rowActionClass(on) {
  return cn(
    "absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md transition-[opacity,color,background-color] duration-150 hover:bg-stone-200/50 focus-visible:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-800 aria-disabled:cursor-not-allowed aria-disabled:text-gray-300 aria-disabled:hover:bg-transparent",
    on
      ? NAV_CORAL_MARK
      : "text-gray-400 opacity-0 hover:text-gray-900 group-hover/row:opacity-100 [@media(hover:none)]:opacity-100",
  );
}
// A line of small print in place of a control or a list: a view with no
// links yet, or why a control isn't offered.
export const NAV_NOTE = "px-3 py-2 text-xs leading-5 text-gray-500";
// The room a row leaves at its right end for that button.
export const ROW_ACTION_ROOM = "pr-10";

/** A link row on a white sheet: the mobile drawer and the account menu. */
export function sheetRowClass(active) {
  return cn(
    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
    active
      ? "bg-brand-50 font-medium text-brand-800"
      : "text-gray-700 hover:bg-brand-50 hover:text-brand-800",
  );
}
